/**
 * Reader-facing clarification request contract.
 *
 * Naming ruling: #5425 used AskHelixPendingRequestAskDto and
 * ASK_HELIX_PENDING_REQUEST_{KINDS,VALUE_TYPES}; ratified #5779 uses this
 * module's AskHelixInputRequestDto and ASK_HELIX_INPUT_REQUEST_{KINDS,VALUE_TYPES}.
 * #5779 also adds ASK_HELIX_RUN_LIFECYCLES. #5425's runToken, resumable,
 * resumeUnavailableReason, and acceptedSourceClasses are intentionally dropped.
 *
 * Any future numeric field on this wire must arrive as a NumericBinding and pass
 * numericBindingIsEngineBound from ./numeric-binding-engine-bound. A bare number is
 * a fence violation. expectedValueType: 'number' is only a string shape hint.
 */

/**
 * CONFORMANCE SEAM: mirrors apps/api/src/ask-helix/manager-runtime/loop/
 * manager-decision.ts:26 and :37. The mirror is deliberate because packages/lib
 * must not import from apps/api; the API-side unit that first imports this DTO owes
 * the bidirectional conformance test that fails if either closed union changes alone.
 */
export const ASK_HELIX_INPUT_REQUEST_KINDS = Object.freeze([
  'clarification',
  'typed_value',
  'policy_choice',
  'evidence_upload',
  'assumption_authorization',
  'approval',
] as const);

export type AskHelixInputRequestKindDto =
  (typeof ASK_HELIX_INPUT_REQUEST_KINDS)[number];

export const ASK_HELIX_INPUT_REQUEST_VALUE_TYPES = Object.freeze([
  'string',
  'number',
  'boolean',
  'file',
] as const);

export type AskHelixInputRequestValueTypeDto =
  (typeof ASK_HELIX_INPUT_REQUEST_VALUE_TYPES)[number];

export const ASK_HELIX_RUN_LIFECYCLES = Object.freeze([
  'paused_for_user',
  'answered_with_pending',
] as const);

export type AskHelixRunLifecycleDto = (typeof ASK_HELIX_RUN_LIFECYCLES)[number];

type HasNumericMember<Value> = Extract<Value, number> extends never ? false : true;

/**
 * Maps every scalar property containing a number to never. This is deliberately a
 * shallow scalar-field fence: options is this DTO's one non-scalar field and has
 * its own explicit runtime boundary checks rather than a general recursive guard.
 */
export type NoNumericField<T extends object> = {
  readonly [Key in keyof T]: HasNumericMember<T[Key]> extends true ? never : T[Key];
};

type AskHelixInputRequestDtoFields = {
  requestId: string;
  kind: AskHelixInputRequestKindDto;
  question: string;
  whyItMatters: string;
  expectedValueType?: AskHelixInputRequestValueTypeDto;
  unit?: string;
  options?: readonly string[];
  blocking: boolean;
};

/** The closed reader allowlist; all manager-only fields are excluded. */
export type AskHelixInputRequestDto = NoNumericField<AskHelixInputRequestDtoFields>;

export const ASK_HELIX_INPUT_REQUEST_DTO_FIELDS = [
  'requestId',
  'kind',
  'question',
  'whyItMatters',
  'expectedValueType',
  'unit',
  'options',
  'blocking',
] as const;

type Assert<T extends true> = T;
type RequestDtoField = (typeof ASK_HELIX_INPUT_REQUEST_DTO_FIELDS)[number];
type RequestDtoHasNoExtraFields = Assert<
  Exclude<keyof AskHelixInputRequestDto, RequestDtoField> extends never ? true : false
>;
type RequestDtoHasNoMissingFields = Assert<
  Exclude<RequestDtoField, keyof AskHelixInputRequestDto> extends never ? true : false
>;

export type AskHelixInputRequestDtoFieldProof = [
  RequestDtoHasNoExtraFields,
  RequestDtoHasNoMissingFields,
];
